import { useQueryClient } from '@tanstack/react-query';

import useAIJobChannel from 'hooks/useAIJobChannel';
import { Jobbable } from 'lib/aiJobHelpers';
import { clone } from 'lib/util';
import Attachment from 'models/Attachment';
import Authorization from 'models/reviews/Authorization';
import Referral from 'models/reviews/Referral';
import Summarization from 'models/Summarization';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';

type JobbableAttachmentJobSubscriberProps = {
  attachment: Attachment;
  jobbable: Jobbable;
};

export default function JobbableAttachmentJobSubscriber({
  attachment,
  jobbable,
}: JobbableAttachmentJobSubscriberProps) {
  const queryClient = useQueryClient();
  const job = attachment.extractDataAIJob;

  const isSummarization = jobbable instanceof Summarization;
  const isAuthorization = jobbable instanceof Authorization;

  const queryKeys = isSummarization
    ? summarizationQueryKeys
    : isAuthorization
      ? authorizationQueryKeys
      : referralQueryKeys;

  const klass = isSummarization ? Summarization : isAuthorization ? Authorization : Referral;

  useAIJobChannel({
    aiJobId: job?.isProcessing ? job.id : undefined,
    onMessageReceived: (updatedJob) => {
      if (updatedJob.isProcessing) return;
      queryClient.setQueryData(queryKeys.show({ id: jobbable.id }), (oldData: Jobbable) => {
        if (!oldData?.attachments) return oldData;

        const updatedAttachments = oldData.attachments.map((att) => {
          if (att.id === updatedJob.aiJobbableId) att.extractDataAIJob = updatedJob;
          return att;
        });

        return clone(klass, oldData, { attachments: updatedAttachments });
      });
    },
  });

  return null;
}
